// @flow
/** @jsx isolateComponent */

import { useEffect, useRef } from 'react';

import {
  useMaybeSendFullStoryEvent,
  useMaybeTrackEvent,
} from '@zapier/toolbox-tracking';

import ClickPropagationBarrier from '@zapier/common-components/src/components/ClickPropagationBarrier';
import Icon from '@zapier/common-components/src/components/Icon';
import Portal from '@zapier/common-components/src/components/Portal';
import { Colors, Zindexes } from '@zapier/theme';
import {
  createKeyHandler,
  isolateComponent,
  labelStyles,
  makeNodesReachable,
  makeNodesUnreachable,
  selectAllElementsWithSelectors,
} from '@zapier/common-utils';

import useDisableScrollOnShow from './useDisableScrollOnShow';
import useFocusOnShow from './useFocusOnShow';

import type { Node } from 'react';

export type Props = {
  /**
   *  Give the modal an accessible description by providing
   *  the ID here of the node that describes the
   *  modal content. Usually one of its children passed
   *  through props.children
   */
  ariaDescribedBy?: string,
  /**
   *  Give the modal a title by providing an ID of the node
   *  that holds the modal title.
   */
  ariaLabelledBy?: string,
  /** The background color of the modal content container */
  backgroundColor?: string,
  /** The color of the close button */
  closeButtonColor?: string,
  /** Optionally define a max width */
  maxWidth?: number,
  /**  Modal Content */
  children: Node,
  /** Should the modal close when the user clicks outside of it? */
  closeOnOutsideClick?: boolean,
  /** The name of the database event to emit when the modal renders */
  eventName?: string,
  /** The name of the custom FullStory event to emit when the modal renders */
  fullStoryEventName?: string,
  /** Optionally let the modal handle its close function */
  onClose?: () => void,
  /** Overflow X behavior of the modal */
  overflowX?: 'scroll' | 'visible' | 'hidden' | 'auto',
  /** Overflow Y behavior of the modal */
  overflowY?: 'scroll' | 'visible' | 'hidden' | 'auto',
  /** Padding for the content of the modal */
  padding?: number | string,
  /** Margin for the content of the modal. Keeps the modal from being edge-to-edge on mobile. */
  margin?: number | string,
  /** Optionally use the modal to render a custom close button */
  renderCloseButton?: (props: *) => Node,
};

const Styles = labelStyles(__filename, {
  root: {
    alignItems: 'center',
    background: 'rgba(53, 65, 71, 0.9)',
    bottom: 0,
    display: 'flex',
    justifyContent: 'center',
    left: 0,
    position: 'fixed',
    right: 0,
    top: 0,
    zIndex: Zindexes.modalOverlay,
  },
  defaultCloseButton: {
    border: 'none',
    color: Colors.steel,
    cursor: 'pointer',
    padding: 20,
    position: 'absolute',
    right: 0,
    top: 0,
    transition: 'all 200ms ease',

    ':hover': {
      color: Colors.batmobile,
    },
    ':focus': {
      outline: '-webkit-focus-ring-color auto 5px',
    },
  },
  inner: ({
    backgroundColor,
    margin,
    padding,
    overflowX,
    overflowY,
    maxWidth,
  }: Props) => {
    const marginNum =
      typeof margin === 'string' ? Number(margin.replace('px', '')) : margin;

    return {
      background: backgroundColor || Colors.white,
      borderRadius: 5,
      maxHeight: marginNum ? `calc(100vh - ${marginNum * 2}px)` : '100vh',
      maxWidth,
      overflowX,
      overflowY,
      margin,
      padding,
      position: 'relative',
      width: '100%',
    };
  },
});

const noop = () => null;

const selectElementsToMask = () => {
  return selectAllElementsWithSelectors([
    '#react-root-app',
    '#header',
    '#footer',
    '.topbar-holder',
    '#support',
  ]);
};

const Modal = (props: Props) => {
  const modalRef = useRef(null);

  useDisableScrollOnShow();
  useFocusOnShow(modalRef);

  useMaybeTrackEvent(!!props.eventName, props.eventName);

  useMaybeSendFullStoryEvent(
    !!props.fullStoryEventName,
    props.fullStoryEventName
  );

  useEffect(() => {
    if (window.self === window.top && document) {
      /*
        When the component mounts (and is not in an iframe)
        set all the DOM elements behind the modal to tabindex="-1"
        and aria-hidden="true" so that the focus cannot leave the modal.
      */
      makeNodesUnreachable(selectElementsToMask());
    }
    return () => {
      /*
        Reset all the nodes that were switched to tabindex="-1"
        and aria-hidden="true"
      */
      makeNodesReachable(selectElementsToMask());
    };
  });

  const maybeRenderCloseButton = () => {
    const shouldRenderDefaultButton = !!props.onClose;
    const maybeRenderDefaultButton = shouldRenderDefaultButton && (
      <button
        aria-label="close"
        css={Styles.defaultCloseButton}
        onClick={e => {
          e.stopPropagation();
          if (props.onClose) {
            props.onClose();
          }
        }}
      >
        <Icon color={props.closeButtonColor} name="delete" />
      </button>
    );

    return typeof props.renderCloseButton === 'function'
      ? props.renderCloseButton(props)
      : maybeRenderDefaultButton;
  };

  return (
    <Portal>
      <div
        css={Styles.root}
        onClick={props.closeOnOutsideClick ? props.onClose : null}
        onKeyDown={createKeyHandler(['escape'], props.onClose || noop)}
        role="presentation"
      >
        <ClickPropagationBarrier css={Styles.inner(props)}>
          <div
            aria-describedby={props.ariaDescribedBy}
            aria-labelledby={props.ariaLabelledBy}
            aria-modal={true}
            ref={modalRef}
            role="dialog"
          >
            {props.children}
            {maybeRenderCloseButton()}
          </div>
        </ClickPropagationBarrier>
      </div>
    </Portal>
  );
};

Modal.defaultProps = {
  maxWidth: 700,
  margin: 0,
  padding: 40,
  overflowX: 'hidden',
  overflowY: 'auto',
};

export default Modal;
